import { useState } from 'react'
import { Link } from 'react-router-dom'
import {
  useBulkCancelJobs,
  useCancelJob,
  useJobs,
  useLocales,
  useProjects,
  useRetryJob,
} from '../api/hooks'
import type { Job, JobStatus } from '../api/types'
import { AiHealthBanners } from '../components/AiHealthBanners'
import { labelMap } from '../lib/locales'

const STATUSES: (JobStatus | 'all')[] = ['all', 'pending', 'running', 'failed', 'cancelled', 'done']

const STATUS_STYLE: Record<JobStatus, string> = {
  pending: 'border-ink-300 bg-ink-100 text-ink-700',
  running: 'border-blue-300 bg-blue-50 text-blue-800',
  done: 'border-emerald-300 bg-emerald-50 text-emerald-800',
  failed: 'border-rose-300 bg-rose-50 text-rose-900',
  cancelled: 'border-ink-300 bg-ink-100 text-ink-500',
}

function timeAgo(iso: string): string {
  const seconds = Math.max(1, Math.round((Date.now() - new Date(iso).getTime()) / 1000))
  if (seconds < 60) return `${seconds}s ago`
  const minutes = Math.round(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  return `${Math.round(hours / 24)}d ago`
}

export function Jobs() {
  const [filter, setFilter] = useState<JobStatus | 'all'>('all')
  const jobs = useJobs(filter === 'all' ? undefined : filter, 200)
  const allJobs = useJobs(undefined, 200)
  const locales = useLocales()
  const retry = useRetryJob()
  const cancel = useCancelJob()
  const bulkCancel = useBulkCancelJobs()
  const labels = labelMap(locales.data ?? [])
  const counts = jobs.data?.counts ?? {}
  const allCounts = allJobs.data?.counts ?? {}

  return (
    <div className="h-full overflow-y-auto bg-ink-50 px-4 py-4">
      <div className="mx-auto max-w-6xl space-y-3">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <div>
            <h1 className="text-base font-semibold text-ink-900">AI job queue</h1>
            <p className="text-xs text-ink-600">
              Every translate job the AI worker has claimed, batched or written, across every
              platform and locale.
            </p>
          </div>
          <BulkCancelButton counts={allCounts} busy={bulkCancel.isPending} onConfirm={() => bulkCancel.mutate({ allPending: true })} />
        </div>

        <AiHealthBanners jobs={allJobs.data?.jobs ?? []} />

        <div className="flex flex-wrap gap-1.5 text-xs" role="tablist" aria-label="Filter by status">
          {STATUSES.map((status) => (
            <button
              key={status}
              role="tab"
              aria-selected={filter === status}
              className={`chip border ${filter === status ? 'border-blue-600 bg-blue-600 text-white' : 'border-ink-200 bg-white text-ink-700 hover:bg-ink-50'}`}
              onClick={() => setFilter(status)}
            >
              {status}
              {status !== 'all' && counts[status] ? ` (${counts[status]})` : ''}
            </button>
          ))}
        </div>

        {jobs.isError && (
          <p role="alert" className="rounded border border-rose-300 bg-rose-50 px-3 py-2 text-xs text-rose-900">
            Could not load jobs: {(jobs.error as Error).message}
          </p>
        )}

        <div className="overflow-hidden rounded-lg border border-ink-200 bg-white">
          <table className="w-full text-xs">
            <thead className="bg-ink-100 text-2xs uppercase tracking-wider text-ink-600">
              <tr>
                <th className="px-3 py-2 text-left font-semibold">Status</th>
                <th className="px-3 py-2 text-left font-semibold">Target</th>
                <th className="px-3 py-2 text-left font-semibold">Locale</th>
                <th className="px-3 py-2 text-left font-semibold">Attempts</th>
                <th className="px-3 py-2 text-left font-semibold">Last error</th>
                <th className="px-3 py-2 text-left font-semibold">Updated</th>
                <th className="px-3 py-2 text-left font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-100">
              {(jobs.data?.jobs ?? []).map((job) => (
                <JobRow
                  key={job.id}
                  job={job}
                  localeLabel={labels[job.locale]?.short ?? job.locale}
                  onRetry={() => retry.mutate(job.id)}
                  retryBusy={retry.isPending}
                  onCancel={() => cancel.mutate(job.id)}
                  cancelBusy={cancel.isPending}
                />
              ))}
              {jobs.isSuccess && jobs.data.jobs.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-3 py-4 text-center text-ink-600">
                    No {filter === 'all' ? '' : filter} jobs.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

/** States how many jobs it will cancel and asks for confirmation — cancelling
 *  thousands of jobs at once must never be a single accidental click. */
function BulkCancelButton({
  counts,
  busy,
  onConfirm,
}: {
  counts: Record<string, number>
  busy: boolean
  onConfirm: () => void
}) {
  const pending = counts.pending ?? 0
  const running = counts.running ?? 0
  const total = pending + running
  if (total === 0) return null

  const handleClick = () => {
    const detail =
      running > 0
        ? `${pending} pending and ${running} running job(s)`
        : `${pending} pending job(s)`
    const warning =
      `Cancel ${detail} across every platform and locale?\n\n` +
      (running > 0
        ? 'Running jobs already mid-call to the AI provider will have their result discarded rather than written. '
        : '') +
      'Nothing is destroyed — every cancelled job can be retried afterwards.'
    if (!window.confirm(warning)) return
    onConfirm()
  }

  return (
    <button className="btn shrink-0" disabled={busy} onClick={handleClick}>
      Cancel {total} pending job{total === 1 ? '' : 's'}
    </button>
  )
}

function JobRow({
  job,
  localeLabel,
  onRetry,
  retryBusy,
  onCancel,
  cancelBusy,
}: {
  job: Job
  localeLabel: string
  onRetry: () => void
  retryBusy: boolean
  onCancel: () => void
  cancelBusy: boolean
}) {
  const canRetry = job.status === 'failed' || job.status === 'cancelled'
  const canCancel = job.status === 'pending' || job.status === 'running'
  return (
    <tr>
      <td className="px-3 py-2">
        <span className={`chip border ${STATUS_STYLE[job.status]}`}>{job.status}</span>
        {job.force && (
          <span className="ml-1 chip border-indigo-300 bg-indigo-50 text-indigo-800" title="Forced — allowed to overwrite a manual translation">
            forced
          </span>
        )}
      </td>
      <td className="px-3 py-2">
        <JobTarget job={job} />
      </td>
      <td className="px-3 py-2">
        <span className="chip border-ink-200 bg-ink-50 text-ink-700">{localeLabel}</span>
      </td>
      <td className="px-3 py-2 tabular-nums text-ink-700">{job.attempts}</td>
      <td className="max-w-xs px-3 py-2 text-ink-700">
        {job.error ? <span title={job.error}>{job.error}</span> : <span className="text-ink-500">—</span>}
      </td>
      <td className="px-3 py-2 text-ink-600">{timeAgo(job.updated_at)}</td>
      <td className="px-3 py-2">
        <div className="flex gap-1.5">
          {canRetry && (
            <button className="btn" disabled={retryBusy} onClick={onRetry}>
              Retry
            </button>
          )}
          {canCancel && (
            <button
              className="btn"
              disabled={cancelBusy}
              onClick={() => {
                const warning =
                  job.status === 'running'
                    ? 'Cancel this job? It may already be mid-call to the AI provider — its result will be discarded rather than written. It can be retried afterwards.'
                    : 'Cancel this pending job? It can be retried afterwards.'
                if (window.confirm(warning)) onCancel()
              }}
            >
              Cancel
            </button>
          )}
        </div>
      </td>
    </tr>
  )
}

/** Platform / project / key, plus a deep link that jumps the editor straight
 *  to this job's cell (consumed by Editor.tsx via `?key=&locale=`). */
function JobTarget({ job }: { job: Job }) {
  const projects = useProjects(job.platform ?? undefined)
  const project = projects.data?.find((item) => item.id === job.project_id)
  const projectSlug = project ? (project.is_default ? 'default' : project.slug ?? 'default') : 'default'

  return (
    <div className="min-w-0">
      <div className="flex items-center gap-1 font-mono text-2xs text-ink-600">
        <span>{job.platform ?? '—'}</span>
        <span aria-hidden="true">/</span>
        <span>{project?.name ?? (job.platform ? '…' : 'unknown project')}</span>
      </div>
      <div className="truncate font-mono text-xs text-ink-800" title={job.key_name ?? job.key_id}>
        {job.key_name ?? job.key_id}
      </div>
      {job.platform && (
        <Link
          to={`/p/${job.platform}/${projectSlug}?key=${encodeURIComponent(job.key_id)}&locale=${encodeURIComponent(job.locale)}`}
          className="text-2xs text-blue-700 underline hover:no-underline"
        >
          Open in editor
        </Link>
      )}
    </div>
  )
}
